import React, { useState, useEffect, useRef } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import { Typography, Paper, Box } from '@mui/material';
import { useAuth } from '../hooks/useAuth';

const navy = '#1A3A6B';

const COLUMNS = [
  { id: 'eventName',  label: 'Event Name' },
  { id: 'eventYear',  label: 'Event Year' },
  { id: 'donationAmount', label: 'Total Amount' },
  { id: 'createdAt',  label: 'Transaction Date' },
];

function getField(row, key) {
  switch (key) {
    case 'eventName':      return row.eventTypesEntity.name;
    case 'eventYear':      return row.eventTypesEntity.eventYear;
    case 'donationAmount': return row.donationAmount;
    case 'createdAt':      return row.createdAt;
    default:               return '';
  }
}

function descendingComparator(a, b, orderBy) {
  const aVal = getField(a, orderBy);
  const bVal = getField(b, orderBy);
  if (bVal < aVal) return -1;
  if (bVal > aVal) return 1;
  return 0;
}

export default function DonationHistory() {
  const { accessToken } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;
  const [donationHistory, setDonationHistory] = useState([]);

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (accessToken) DonationHistoryFetch();
  }, [accessToken]);

  useEffect(() => {
    if (tableContainerRef.current) tableContainerRef.current.scrollLeft = 0;
  }, [donationHistory]);

  const DonationHistoryFetch = async () => {
    try {
      const response = await fetch(`${API_URL}/gquery`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-cmdfw-jwt-token': `${accessToken}`,
        },
        body: JSON.stringify({
          query: `
            query {
              donationHistory {
                donations {
                  transactionId
                  eventTypesEntity { eventTypeId name eventYear }
                  donationAmount
                  paymentStatus
                  createdAt
                  createdBy
                }
              }
            }
          `,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setDonationHistory(data.data.donationHistory.donations);
      }
    } catch (error) {
      console.error('Error fetching Donation History:', error);
    }
  };

  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
    setPage(0);
  };

  const sortedRows = [...donationHistory].sort((a, b) =>
    order === 'desc'
      ? descendingComparator(a, b, orderBy)
      : -descendingComparator(a, b, orderBy)
  );

  const visibleRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Paper elevation={3} sx={{ overflow: 'hidden' }}>
      <Box sx={{ bgcolor: navy, px: 3, py: 2 }}>
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', fontFamily: 'Georgia, serif' }}>
          Donation History
        </Typography>
      </Box>

      <TableContainer ref={tableContainerRef} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 500 }} aria-label="donation history table">
          <TableHead>
            <TableRow sx={{ bgcolor: '#e4eaf0' }}>
              {COLUMNS.map((col) => (
                <TableCell key={col.id} sortDirection={orderBy === col.id ? order : false}
                  sx={{ fontWeight: 700, color: navy, whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                    sx={{
                      '&.Mui-active': { color: navy },
                      '& .MuiTableSortLabel-icon': { color: `${navy} !important` },
                    }}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: '#888' }}>
                  No records found.
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((row, idx) => (
                <TableRow key={row.transactionId ?? idx}
                  sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' }, '&:hover': { bgcolor: '#f0f4f8' } }}>
                  <TableCell>{row.eventTypesEntity.name}</TableCell>
                  <TableCell>{row.eventTypesEntity.eventYear}</TableCell>
                  <TableCell>{row.donationAmount}</TableCell>
                  <TableCell>{row.createdAt}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={donationHistory.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />
    </Paper>
  );
}
